/**
 * The real Conductor v2: realTime derived from AudioContext.currentTime;
 * songTime mirrored from the sim's exact integral; the music's playbackRate
 * is scheduled along the identical piecewise-constant effRate curve anchored
 * to the same ctx timeline (R2 §1.2) — so buffer position and songTime
 * advance together by construction. The pitch shift IS the speed feedback.
 *
 * Rate scheduling: one setValueAtTime per tick WHILE the rate is changing
 * (steps of ≤0.3 % at 120 Hz — inaudible as zipper), nothing while steady.
 * Exact staircase = zero accumulated position drift. Pad steps stay instant
 * jumps on purpose (the tape-speed lurch is the feedback).
 *
 * Pause = ctx.suspend(): currentTime freezes, both clocks freeze with it.
 * Loop wrap: the AudioParam timeline is independent of loop position, so
 * scheduled segments continue across the wrap untouched (amends D-024).
 */
import type { Unsubscribe } from "../core/eventBus";
import { BeatDispatcher } from "./beatDispatcher";
import { ClockAnchor, type Conductor } from "./conductor";
import type { SongAsset } from "../data/songs";
import { SIM_DT, SONG_START_DELAY_S } from "../config/tuning";

export class AudioConductor implements Conductor {
  readonly bpm: number;
  /** ctx time at which realTime = 0 (before latency corrections). ∞ = not started. */
  private zeroCtxTime = Number.POSITIVE_INFINITY;
  private source: AudioBufferSourceNode | null = null;
  private latencyS = 0;
  private userOffsetS = 0;
  private anchor = new ClockAnchor();
  private lastScheduledRate = 1;
  private dispatcher = new BeatDispatcher();
  private readonly offsetS: number;
  private readonly loopEndS: number;

  constructor(
    private readonly ctx: AudioContext,
    private readonly buffer: AudioBuffer,
    song: SongAsset,
    private readonly out: AudioNode,
  ) {
    this.bpm = song.bpm;
    this.offsetS = (song.offsetMs ?? 0) / 1000;
    this.loopEndS = song.durationBeats
      ? this.offsetS + song.durationBeats * (60 / song.bpm)
      : buffer.duration;
  }

  setUserLatencyOffsetMs(ms: number): void {
    this.userOffsetS = ms / 1000;
  }

  get realTime(): number {
    return this.ctx.currentTime - this.zeroCtxTime - this.latencyS - this.userOffsetS;
  }

  get songTime(): number {
    return this.anchor.songTimeAt(this.realTime);
  }

  get beat(): number {
    return (this.songTime * this.bpm) / 60;
  }

  get beatPhase(): number {
    const b = this.beat;
    return b - Math.floor(b);
  }

  restartAt(fromSongTime = 0): void {
    this.stopSource();
    const anyCtx = this.ctx as AudioContext & { outputLatency?: number };
    this.latencyS = (anyCtx.outputLatency ?? 0) + this.ctx.baseLatency;
    this.source = this.ctx.createBufferSource();
    this.source.buffer = this.buffer;
    this.source.loop = true;
    this.source.loopStart = this.offsetS;
    this.source.loopEnd = this.loopEndS;
    this.source.playbackRate.value = 1; // throttle resets to 1.0 on every (re)start
    this.lastScheduledRate = 1;
    this.source.connect(this.out);
    const startCtx = this.ctx.currentTime + SONG_START_DELAY_S;
    // realTime = fromSongTime at the start instant; both clocks agree there
    this.zeroCtxTime = startCtx - fromSongTime - this.latencyS - this.userOffsetS;
    const bufferPos = this.offsetS + fromSongTime;
    if (bufferPos >= 0) {
      this.source.start(startCtx, bufferPos);
    } else {
      this.source.start(startCtx - bufferPos, 0);
    }
    this.anchor.reset(fromSongTime);
    this.dispatcher.resetTo((fromSongTime * this.bpm) / 60);
  }

  syncFromSim(tick: number, songTime: number, effRate: number): void {
    this.anchor.sync(tick, songTime, effRate);
  }

  rateChangedAt(tick: number, effRate: number): void {
    if (!this.source || effRate === this.lastScheduledRate) return;
    // ctx time of the tick on the same timeline realTime is measured on
    const ctxAt =
      this.zeroCtxTime + tick * SIM_DT + this.latencyS + this.userOffsetS;
    this.source.playbackRate.setValueAtTime(
      effRate,
      Math.max(ctxAt, this.ctx.currentTime),
    );
    this.lastScheduledRate = effRate;
  }

  tick(_realDtSeconds: number): void {
    const b = this.beat;
    if (b >= 0) this.dispatcher.dispatch(b);
  }

  songTimeAt(realT: number): number {
    return this.anchor.songTimeAt(realT);
  }

  onBeat(subdivision: number, cb: (beatIndex: number) => void): Unsubscribe {
    return this.dispatcher.on(subdivision, cb);
  }

  private stopSource(): void {
    if (this.source) {
      try {
        this.source.stop();
      } catch {
        // already ended — fine
      }
      this.source.disconnect();
      this.source = null;
    }
  }

  stop(): void {
    this.stopSource();
    this.dispatcher.clear();
  }
}
